import { BlockTitle } from "@blocks/Blocks";
import Ad from "@components/Advertisement/Ad";
import AdSettings from "@components/Advertisement/AdSettings";
import Container from "@components/Container/Container";
import SeoFooter from "@components/Footer/SeoFooter/SeoFooter";
import GAData from "@components/GAData/GAData";
import Header from "@components/Header/Header";
import MetaTags from "@components/MetaTags/MetaTags";
import Booklet from "@pages/Keyword/templates/CampaignTemplate/components/Booklet";
import CampaignTemplateHeader from "@pages/Keyword/templates/CampaignTemplate/components/CampaignTemplateHeader";
import Highlights from "@pages/Keyword/templates/CampaignTemplate/components/Highlights";
import Infographics from "@pages/Keyword/templates/CampaignTemplate/components/Infographics";
import LatestNews from "@pages/Keyword/templates/CampaignTemplate/components/LatestNews";
import Podcasts from "@pages/Keyword/templates/CampaignTemplate/components/Podcasts";
import Videos from "@pages/Keyword/templates/CampaignTemplate/components/Videos";
import Views from "@pages/Keyword/templates/CampaignTemplate/components/Views";
import Whatsapp from "@pages/Keyword/templates/CampaignTemplate/components/Whatsapp";
import { getFormattedTextForAds } from "@pages/Section/helpers";
import { TagType } from "@pages/Section/types";
import Listing from "@particles/listing/Listing";
import { Tag } from "@typings/Cue";
import { sectionNavigationItems } from "@util/constant";
import { useDetectAdBlock } from "adblock-detect-react";

import { CampaignTemplateKeywordContext } from "./types";

export type CampaignTemplateBudgetProps = Pick<
  CampaignTemplateKeywordContext,
  "data"
>;

/**
 * This is the code to display components for "CAMPAIGN TEMPLATE 1" .
 * https://www.figma.com/design/QCEPjsj7Sdm5KqnY3ta5tq/TEMPLATES--WEB---APP-?node-id=1-12472&t=NMzsh6SczSQjIa80-4
 */
const CampaignTemplateBudget = ({
  data: {
    entity: { tag },
    title,
    logo,
    highlights,
    views,
    podcasts,
    videoData,
    latestNewsProps,
    relatedStoriesProps,
    attachments,
    seoFooterData,
  },
}: CampaignTemplateBudgetProps): React.ReactElement => {
  const adBlockDetected = useDetectAdBlock();

  const termName = tag.name;
  const termPath = tag.urlPath;
  const infographics = attachments?.[0];
  const mtiBooklet = attachments?.[1];
  const { id, name, urlPath } = tag;

  const feedTag: Tag = {
    aliases: [],
    fields: [],
    id: id.toString(),
    name,
    type: TagType.Keyword,
    urlPath,
    sections: [],
    uri: "",
    urlPathHistory: [],
    parent: null,
  };

  return (
    <div className="w-full" data-testid="campaign-template-budget-component">
      <AdSettings
        adNames={["lb1", "imu2", "prestitial", "catfish"]}
        path={termPath}
        adTargets={[
          { key: "page", value: "listing" },
          { key: "bttags", value: getFormattedTextForAds(termName) },
        ]}
      />

      <GAData
        chapter1={termName}
        level2={"keywords"}
        title={"keywords"}
        adblocker={adBlockDetected ? 1 : 0}
      />

      <MetaTags
        title={title}
        description={`THE BUSINESS TIMES ${termName} - Find ${termName} News & Headlines, insight and analysis in Singapore, Asia-Pacific & global markets news at The Business Times.`}
        ogType="article"
        slug={`/${termPath}`}
      />

      <Ad
        adType="lb1"
        className="flex min-h-[calc(50px+0.50rem)] flex-col items-center justify-center border-b border-gray-175 bg-gray-125 py-1 text-center md:py-3 lg:min-h-[calc(250px+1.5rem)]"
      />

      <Header
        parentCategory="keywords"
        childCategory={termPath.replace("/keywords/", "")}
        categoryDataMapping={sectionNavigationItems}
      />

      <Container rootClassName="pt-8 md:px-0">
        <CampaignTemplateHeader feedTag={feedTag} logo={logo} />
        <>
          {highlights.props.length > 0 ? (
            <div className="flex flex-col gap-x-4 lg:flex-row">
              <div className="w-full lg:w-8/12">
                {highlights ? <Highlights highlights={highlights} /> : null}
              </div>

              <div className="mb-10 md:grid md:grid-cols-2 md:gap-x-4 lg:block lg:w-4/12">
                {infographics ? (
                  <Infographics infographics={infographics} />
                ) : null}

                {latestNewsProps ? (
                  <LatestNews
                    articleProps={latestNewsProps}
                    className="mb-4 border-t border-gray-850 pt-2 md:row-span-2"
                  />
                ) : null}

                {mtiBooklet ? (
                  <Booklet
                    className="border-t border-gray-850 pt-2"
                    mtiBookletData={mtiBooklet}
                  />
                ) : null}
              </div>
            </div>
          ) : null}
        </>

        <>
          {views?.props?.length > 0 ? (
            <div className="mb-10 flex flex-col gap-x-4 lg:flex-row">
              <div className="w-full lg:w-8/12">
                <Views
                  views={views}
                  className="border-top border-gray-850 pb-10 pt-3 lg:pb-0"
                />
              </div>

              <div className="mx-auto w-full md:w-1/2 lg:w-4/12">
                <Whatsapp />
              </div>
            </div>
          ) : null}
        </>

        <>
          {videoData?.items?.length > 0 ? (
            <Videos
              videoData={videoData}
              className="border-top mb-10 border-gray-850 pt-2"
            />
          ) : null}
        </>

        <>
          {podcasts?.props?.length > 0 ? (
            <div className="mb-10">
              <Podcasts props={podcasts.props} title={podcasts.title} />
            </div>
          ) : null}
        </>

        <>
          {relatedStoriesProps ? (
            <div className="mb-10 flex flex-col lg:flex-row">
              <div className="border-top mb-6 w-full border-gray-850 lg:w-8/12">
                <BlockTitle
                  text="RELATED STORIES"
                  rootClassName="mb-4 mt-2 text-lg md:text-3xl"
                />

                <Listing
                  cardProps={relatedStoriesProps}
                  urlPath={urlPath}
                  pageType={"keyword"}
                  size={10}
                />
              </div>

              <div className="mt-10 w-full lg:w-4/12">
                <Ad
                  adType="imu2"
                  className="flex items-center justify-center lg:sticky lg:top-20"
                />
              </div>
            </div>
          ) : null}
        </>
      </Container>

      <SeoFooter data={seoFooterData} />

      <Ad adType="catfish" />

      <Ad adType="prestitial" />
    </div>
  );
};

export default CampaignTemplateBudget;
